// src/components/forms/RuleEngine.ts

import { RuleType, Rule } from "../../types/interfaces";
import { FormContextState } from "./formTypes";

// ========== RUNTIME STORAGE ==========
let rules: Rule[] = [];
let hookInvoker: RuleActionInvoker | null = null;
let formContext: FormContextState | null = null;

export type RuleActionInvoker = (
    action: RuleType,
    target: string,
    value?: any
) => void;

export function registerRules(newRules: Rule[]) {
    rules = [...rules, ...newRules];
}

export function clearRules() {
    rules = [];
}

export function setRuleActionInvoker(invoker: RuleActionInvoker) {
    hookInvoker = invoker;
}

export function bindFormContext(ctx: FormContextState) {
    formContext = ctx;
}

// ========== EVALUATE ALL RULES ==========
export function evaluateRules(formData: Record<string, any>) {
    for (const rule of rules) {
        try {
            const shouldRun = !rule.condition || evalCondition(rule.condition, formData);

            if (shouldRun) {
                executeRule(rule, formData);
            }
        } catch (err) {
            console.warn("❌ Rule evaluation failed", rule, err);
        }
    }
}

export function runRuleManually(rule: Rule, formData: Record<string, any>) {
    try {
        if (!rule.condition || evalCondition(rule.condition, formData)) {
            executeRule(rule, formData);
        }
    } catch (err) {
        console.warn("⚠️ Manual rule failed:", rule, err);
    }
}

// ========== CORE EVALUATOR ==========

function evalCondition(expression: string, context: any): boolean {
    const safeExpr = sanitizeExpression(expression);
    return Function("data", `with(data) { return ${safeExpr}; }`)(context);
}

function sanitizeExpression(expr: string): string {
    return expr
        .replace(/[^a-zA-Z0-9_().<>=!'" \-+*/&|?:,[\]]/g, "")
        .replace(/\b(or)\b/gi, "||")
        .replace(/\b(and)\b/gi, "&&")
        .replace(/\b(not)\b/gi, "!");
}

// ========== RULE EXECUTION ==========

function executeRule(rule: Rule, formData: Record<string, any>) {
    const { type, target, value } = rule;

    if (formContext) {
        switch (type) {
            case "show":
                return formContext.setFieldVisible(target, true);

            case "hide":
                return formContext.setFieldVisible(target, false);

            case "enable":
                return formContext.setFieldReadonly(target, false);

            case "disable":
                return formContext.setFieldReadonly(target, true);

            case "set":
                return formContext.setValue(target, value);

            case "calculate":
                try {
                    const calcValue = evalExpressionAsValue(value, formData);
                    return formContext.setValue(target, calcValue);
                } catch (e) {
                    console.warn("Error calculating field value:", e);
                }
                break;

            case "validate":
                if (evalCondition(rule.condition!, formData)) {
                    return formContext.setError(target, value || "Invalid input");
                } else {
                    return formContext.setError(target, undefined);
                }

            case "trigger":
                return hookInvoker?.(type, target, value);

            case "navigate":
                return hookInvoker?.(type, target, value);
        }
    } else if (hookInvoker) {
        // Fallback to generic hook if no context
        hookInvoker(type, target, value);
    } else {
        console.warn("⚠️ No form context or hook defined for rules");
    }
}

// ========== EXPRESSION VALUE EVALUATOR ==========

function evalExpressionAsValue(expression: string, context: Record<string, any>): any {
    const safeExpr = sanitizeExpression(expression);
    return Function("data", `with(data) { return ${safeExpr}; }`)(context);
}
